import React from 'react';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { ServiceDetailPage } from '@/components/service-detail-page';
import { SEO } from '@/components/seo';

const aiIntegrationService = {
  name: "AI Integration",
  promise: "Intelligent automation that learns, adapts, and delivers measurable ROI",
  primaryKPI: "60% reduction in manual tasks within 90 days",
  signals: {
    bestFor: "Enterprises looking to scale operations without scaling headcount",
    timeline: "4-8 weeks from concept to deployment",
    team: "ML engineer + solution architect + integration specialist",
    deliverables: "AI models + integrations + dashboard"
  },
  outputs: [
    "Custom AI model training and deployment",
    "API integration with existing systems",
    "Real-time analytics dashboard",
    "Automated workflow optimization",
    "Continuous learning and improvement system",
    "Complete documentation and training"
  ],
  process: [
    {
      step: "Discovery & Use Case Analysis",
      description: "Identify high-impact automation opportunities by analyzing workflows and data",
      ownership: "We lead analysis, you provide business context",
      artifacts: ["Use case prioritization", "ROI projection", "Technical requirements"]
    },
    {
      step: "Model Selection & Training",
      description: "Choose optimal AI models and train them on your specific data",
      ownership: "We build and train, you validate accuracy",
      artifacts: ["Trained models", "Performance metrics", "Training documentation"]
    },
    {
      step: "Integration & Testing",
      description: "Integrate AI with your tech stack and ensure reliability",
      ownership: "We integrate, you test in staging",
      artifacts: ["API endpoints", "Integration guide", "Test results"]
    },
    {
      step: "Deployment & Optimization",
      description: "Launch with continuous monitoring and improvement",
      ownership: "We deploy and monitor, you provide feedback",
      artifacts: ["Production deployment", "Analytics dashboard", "Performance reports"]
    }
  ],
  proof: [
    {
      client: "Manufacturing company",
      challenge: "Manual quality control was slow and inconsistent",
      result: "Automated visual inspection with computer vision AI",
      metric: "60% reduction in quality control time"
    },
    {
      client: "Financial services firm",
      challenge: "Fraud detection was missing subtle patterns",
      result: "Deployed predictive AI models for transaction monitoring",
      metric: "85% improvement in fraud detection accuracy"
    },
    {
      client: "Healthcare provider",
      challenge: "Radiologists overwhelmed with imaging workload",
      result: "Implemented medical imaging AI to assist diagnosis",
      metric: "40% faster patient diagnosis"
    }
  ],
  faqs: [
    {
      question: "What AI technologies do you use?",
      answer: "We use modern AI technologies including GPT models, computer vision (YOLO, ResNet), predictive analytics, natural language processing, and custom machine learning models. We select the best technology for your specific use case, whether that's OpenAI, TensorFlow, PyTorch, or proprietary models."
    },
    {
      question: "How long until we see ROI?",
      answer: "Most clients see measurable results within 60-90 days. Initial efficiency gains appear during testing, with full ROI typically achieved within 6-12 months depending on implementation scope. We track KPIs from day one to demonstrate value."
    },
    {
      question: "Can AI integrate with our legacy systems?",
      answer: "Yes. We specialize in integrating modern AI with existing infrastructure through APIs, middleware, and custom connectors. We've successfully integrated with ERPs, CRMs, databases, and proprietary systems across various industries."
    },
    {
      question: "What data do you need to train the models?",
      answer: "Requirements vary by use case. Generally, we need historical data relevant to the task (documents, images, transactions, etc.). We can work with limited datasets using transfer learning and data augmentation techniques. All data handling is secure and compliant."
    },
    {
      question: "How do you ensure AI accuracy and reliability?",
      answer: "We implement rigorous testing protocols, continuous monitoring, and human-in-the-loop validation where needed. Models are regularly retrained on new data, and we set up alert systems for performance degradation. We also provide confidence scores and explainable AI outputs."
    }
  ]
};

const AIIntegration = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="AI Integration Services - Custom AI Solutions & Automation | AffinityAim"
        description="AI integration reducing operational costs by 34%. ChatGPT, LLMs, predictive analytics, automation. Transform your business with AI."
        canonical="https://www.affinityaim.com/services/ai-integration"
        keywords="AI integration services, AI automation, machine learning consulting, ChatGPT integration"
        type="service"
      />
      <Navigation />
      <main className="pt-32">
        <ServiceDetailPage service={aiIntegrationService} />
      </main>
      <Footer />
    </div>
  );
};

export default AIIntegration;
